import React, { useState } from 'react';
import styles from './SectionCard.module.css';
import type { Section, Option } from '../../types';
import { OptionCard } from '../OptionCard/OptionCard';
import { useLocale } from '../../contexts/LocaleContext';
import { getLocalizedString } from '../../utils/localization';
import { t } from '../../locales/translations';

interface SectionCardProps {
  section: Section;
  options: Option[];
  selectedOptions: Option[];
  onOptionToggle: (option: Option) => void;
  onOptionQuantityChange?: (optionId: string, quantity: number) => void;
  getOptionQuantity?: (optionId: string) => number;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  section,
  options,
  selectedOptions,
  onOptionToggle,
  onOptionQuantityChange,
  getOptionQuantity
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { selectedLocale } = useLocale();

  const sectionOptions = options
    .filter(opt => opt.section_id === section.id)
    .filter(opt => {
      if (!opt.depends_on) return true;
      return selectedOptions.some(selected => selected.id === opt.depends_on);
    });
  const selectedSectionOptions = selectedOptions.filter(opt => opt.section_id === section.id);
  const canSelectMore = section.options_num === undefined || selectedSectionOptions.length < section.options_num;

  return (
    <div className={styles.sectionCard}>
      <div
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className={styles.headerInfo}>
          <h3 className={styles.title}>{getLocalizedString(section.title, selectedLocale)}</h3>
          <div className={styles.badges}>
            {!section.optional && (
              <span className={styles.requiredBadge}>{t('required', selectedLocale)}</span>
            )}
            <span className={styles.selectionInfo}>
              {section.options_num === undefined
                ? `${selectedSectionOptions.length} ${t('selected', selectedLocale)}`
                : `${selectedSectionOptions.length} / ${section.options_num} ${t('selected', selectedLocale)}`
              }
            </span>
          </div>
        </div>
        <button className={styles.expandButton}>
          {isExpanded ? '−' : '+'}
        </button>
      </div>

      {isExpanded && (
        <div className={styles.options}>
          {sectionOptions.map((option) => {
            const isSelected = selectedOptions.some(opt => opt.id === option.id);
            const isDisabled = !isSelected && !canSelectMore;
            const quantity = getOptionQuantity ? getOptionQuantity(option.id) : 1;

            return (
              <OptionCard
                key={option.id}
                option={option}
                isSelected={isSelected}
                isDisabled={isDisabled}
                allowMultiple={section.options_num === undefined || section.options_num > 1}
                quantity={quantity}
                onToggle={() => onOptionToggle(option)}
                onQuantityChange={
                  onOptionQuantityChange && option.allow_multiple_instances
                    ? (qty) => onOptionQuantityChange(option.id, qty)
                    : undefined
                }
              />
            );
          })}
        </div>
      )}

      {!isExpanded && (
        <div className={styles.collapsedOptions}>
          {sectionOptions.map((option) => {
            const isSelected = selectedOptions.some(opt => opt.id === option.id);
            const isDisabled = !isSelected && !canSelectMore;
            const quantity = getOptionQuantity ? getOptionQuantity(option.id) : 1;

            return (
              <button
                key={option.id}
                className={`${styles.optionButton} ${isSelected ? styles.selected : ''} ${isDisabled ? styles.disabled : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDisabled) {
                    onOptionToggle(option);
                  }
                }}
                disabled={isDisabled}
              >
                {getLocalizedString(option.title, selectedLocale)}
                {option.allow_multiple_instances && isSelected && quantity > 1 && (
                  <span className={styles.quantityBadge}> ×{quantity}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};